import { bearing as turfBearing, distance as turfDistance } from '@turf/turf';
import { Position } from 'geojson';
import React from 'react';
import { UnitFormatter } from '../../../../formatters/unit-formatter';
import useMapInteraction from '../../../../hooks/use-map-interaction';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import useMap from '../../../hooks/use-map';
import { useSafeEffect } from '../../../hooks/use-safe-effect';
import { setDistanceBearingTool } from '../../../state/slices/workspace-slice';
import { DistanceBearingPlugin } from '../plugins/distance-bearing-plugin';

type Item = {
    id: number;
    xy: [number, number];
    distance: number;
    bearing: number;
    total: number;
};

let id = 0;

export default function DistanceBearingLayer() {
    const show = useAppSelector((x) => x.workspace.mapTools.distanceBearing);
    const map = useMap();
    const interaction = useMapInteraction();
    const dispatch = useAppDispatch();
    const linePlugin = React.useMemo(() => new DistanceBearingPlugin(), []);
    const [lastPosition, setLastPosition] = React.useState<Position | null>(null);
    const [distance, setDistance] = React.useState(0);
    const [bearing, setBearing] = React.useState(0);
    const [items, setItems] = React.useState<Item[]>([]);
    const [xy, setXy] = React.useState<[number, number]>([0, 0]);

    useSafeEffect(() => {
        if (!map) {
            return;
        }
        linePlugin.onAdd(map);
        return () => {
            map.removePlugin(linePlugin);
        };
    }, [map]);

    React.useEffect(() => {
        if (!show) {
            interaction.setContextMenu('enabled');
            interaction.setTooltips('enabled');
            linePlugin.show = false;
            if (lastPosition) {
                setLastPosition(null);
                setDistance(0);
                setBearing(0);
                setItems([]);
                linePlugin.clear();
            }
            return;
        }

        interaction.setContextMenu('disabled');
        interaction.setTooltips('disabled');
        map!._canvasContainer.style.cursor = 'crosshair';
        linePlugin.show = true;

        const move = map?.on('mousemove', (e) => {
            setXy([e.originalEvent.clientX, e.originalEvent.clientY]);
            const currentPosition: Position = [e.lngLat.lng, e.lngLat.lat];
            if (lastPosition) {
                setDistance(turfDistance(lastPosition, currentPosition, { units: 'nauticalmiles' }));
                setBearing(Math.round(turfBearing(lastPosition, currentPosition)));
                linePlugin.currentPoint = currentPosition;
            }
        });

        const click = map?.on('click', (e) => {
            const currentPosition: Position = [e.lngLat.lng, e.lngLat.lat];
            if (lastPosition) {
                const d = turfDistance(lastPosition, currentPosition, { units: 'nauticalmiles' });
                const item: Item = {
                    id: id++,
                    distance: d,
                    bearing: Math.round(turfBearing(lastPosition, currentPosition)),
                    total: (items.length > 0 ? items[items.length - 1].total : 0) + d,
                    xy: [e.originalEvent.clientX, e.originalEvent.clientY]
                };
                setItems([...items, item]);
                linePlugin.addMarker(currentPosition, item);
            }
            setLastPosition(currentPosition);
            linePlugin.addPoint(currentPosition);
        });

        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                dispatch(setDistanceBearingTool(false));
            }
        };
        document.addEventListener('keydown', handler);

        return () => {
            map!._canvasContainer.style.cursor = 'grab';
            move?.unsubscribe();
            click?.unsubscribe();
            document.removeEventListener('keydown', handler);
        };
    }, [show, items, lastPosition]);

    if (!show) {
        return null;
    }

    return (
        <>
            {!lastPosition ? (
                <div
                    className="pe-none k-card p-1 w-auto"
                    style={{ fontSize: '0.7rem', position: 'fixed', left: xy[0] + 5, top: xy[1] + 5 }}>
                    Select the beginning point
                </div>
            ) : (
                <div
                    className="pe-none k-card p-1 w-auto"
                    style={{ fontSize: '0.7rem', position: 'fixed', left: xy[0] + 5, top: xy[1] + 5 }}>
                    {UnitFormatter.formatDistance(distance)}, {bearing} deg
                </div>
            )}
        </>
    );
}
